import { useCallback } from "react";
import AnswersBuilderBase from "features/Library/Common/QuestionsForm/Answers/AnswersBuilders/AnswersBuilderBase";
import { type ValidationContext, type IAnswerBase, type ValidationValueBase } from "features/Library/Common/models";
import { type AnswersBuilderPropsBase } from "features/Library/Common/props";
import { AnswersMap } from "features/Library/Surveys/SurveyDetails/SurveyContentForm/SurveyProviders/AnswersMap";
import { getAnswerDefaultData } from "features/Library/Surveys/SurveyDetails/SurveyContentForm/SurveyProviders/defaultAnswerProvider";
import { SurveyQuestionOtherAnswerOptions } from "features/Library/Surveys/SurveyDetails/SurveyContentForm/SurveyFields/SurveyQuestionOtherAnswerOptions/SurveyQuestionOtherAnswerOptions";
import {
  type OtherAnswerOptions,
  type SurveyQuestion,
} from "features/Library/Surveys/SurveyDetails/SurveyContentForm/types";
import { type SurveyAnswerType, SurveyQuestionType } from "features/Library/Surveys/types/models";
import { AddSurveyAnswerButton } from "features/Library/Surveys/SurveyDetails/SurveyContentForm/AddSurveyAnswerButton/AddSurveyAnswerButton";
import { AddButton } from "components/buttons/iconButtons/AddButton/AddButton";
import { nameofFactory } from "utils/nameof";

export const SurveyAnswersBuilder = (props: AnswersBuilderPropsBase<SurveyQuestion<unknown>>) => {
  switch (props.question.questionType) {
    case SurveyQuestionType.MultipleChoiceTextQuestion:
    case SurveyQuestionType.SingleChoiceTextQuestion:
      return <AnswerList {...props} />;
    default:
      return <></>;
  }
};

const AnswerList = (props: AnswersBuilderPropsBase<SurveyQuestion<unknown>>) => {
  const { onBlur, onChanged, disabled, question, validatedFieldProps, propertyNamePrefix } = props;

  const optionsNameOf = nameofFactory<OtherAnswerOptions>();
  const nameof = nameofFactory<SurveyQuestion<unknown>>();

  const toggleOtherAnswer = useCallback(
    (includeOtherAsAnswer: boolean) => () =>
      validatedFieldProps.setFieldValue(
        `${propertyNamePrefix}${nameof("otherAnswerOptions")}.${optionsNameOf("includeOtherAsAnswer")}`,
        includeOtherAsAnswer,
      ),
    [nameof, optionsNameOf, propertyNamePrefix, validatedFieldProps],
  );

  const renderAnswer = useCallback(
    (
      answer: IAnswerBase,
      index: number,
      validatedFieldProps: ValidationContext<ValidationValueBase<SurveyQuestion<unknown>>>,
      propertyNamePrefix: string,
    ) => {
      const { Component } = AnswersMap[answer.answerType as SurveyAnswerType];
      return (
        <Component
          answer={answer}
          index={index}
          validatedFieldProps={validatedFieldProps}
          disabled={disabled}
          onBlur={onBlur}
          onChanged={onChanged}
          propertyNamePrefix={propertyNamePrefix}
        />
      );
    },
    [disabled, onBlur, onChanged],
  );

  const renderAdditionalAnswerOptions = useCallback(
    (
      validatedFieldProps: ValidationContext<ValidationValueBase<SurveyQuestion<unknown>>>,
      propertyNamePrefix: string,
    ) => {
      return question.otherAnswerOptions?.includeOtherAsAnswer ? (
        <SurveyQuestionOtherAnswerOptions
          onBlur={onBlur}
          onChanged={onChanged}
          validatedFieldProps={validatedFieldProps}
          otherAnswerOptions={question.otherAnswerOptions}
          disabled={disabled}
          propertyNamePrefix={propertyNamePrefix}
          onRemove={toggleOtherAnswer(false)}
        />
      ) : null;
    },
    [disabled, onBlur, onChanged, question.otherAnswerOptions, toggleOtherAnswer],
  );

  const renderAddButton = useCallback(
    (onClick: () => void) => {
      return <AddSurveyAnswerButton disabled={disabled} onAddAnswer={onClick} question={question} />;
    },
    [question, disabled],
  );

  const renderAddOtherButton = useCallback(() => {
    return !question.otherAnswerOptions?.includeOtherAsAnswer ? (
      <AddButton onClick={toggleOtherAnswer(true)} label={'Add "Other" as answer'} disabled={disabled} />
    ) : null;
  }, [disabled, question.otherAnswerOptions?.includeOtherAsAnswer, toggleOtherAnswer]);

  return (
    <AnswersBuilderBase
      renderAddAnswerButton={renderAddButton}
      renderAddOtherAnswerButton={renderAddOtherButton}
      renderAnswer={renderAnswer}
      renderAdditionalOptions={renderAdditionalAnswerOptions}
      defaultDataProvider={getAnswerDefaultData}
      {...props}
    />
  );
};
